import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';
import { FEATURE_FLAG } from '~/constants/featureFlags';

import { type Address } from '~/types/address';
import { type UUID } from '~/types/common';
import { type EntityType } from '~/types/entity';
import { type Permission } from '~/types/permission';

import AuthService from '~/services/auth.service';
import { vestigasApi } from '~/services/kyClient';

import { queryKeysUser } from './queryKeys';

export type UserData = {
  company: {
    address: Address;
    companyAccountId: UUID;
    id: UUID;
    name: string;
    orgUnitPaths: UUID[][];
    orgUnits: UUID[];
    permissionsFrom: Array<{
      id: number;
      permissions: Permission[];
      subjectId: UUID;
      subjectType:
        | 'accounting_reference'
        | 'company'
        | 'org_unit'
        | 'site'
        | 'user_group'
        | 'user'
        | 'vehicle';
    }>;
    syncKey: string;
  };
  companyAccount: {
    baseOuId: UUID;
    companies: Array<{
      address: Address;
      companyAccountId: UUID;
      id: UUID;
      name: string;
      orgUnitPaths: UUID[][];
      orgUnits: UUID[];
      permissionsFrom: Array<{
        id: number;
        permissions: Permission[];
        subjectId: UUID;
        subjectType:
          | 'accounting_reference'
          | 'company'
          | 'org_unit'
          | 'site'
          | 'user_group'
          | 'user'
          | 'vehicle';
      }>;
      syncKey: string;
    }>;
    data: Record<string, unknown> & {
      featureFlags: Record<string, boolean>;
      supportEmail: string | undefined;
      supportPhoneNumber: string | undefined;
    };
    id: UUID;
    name: string;
  };
  firstName: string;
  featureFlags: Record<string, boolean>;
  isSynced?: boolean;
  isVestigasSupport: boolean;
  lastName: string;
  name: string;
  email: string;
  position: string;
  roles: Array<{
    entityId: UUID;
    entityType: EntityType;
    permission: Permission[];
    role: number[];
    userId: UUID;
  }>;
  settings: Record<string, unknown> & {
    contactInformation?: string;
    featureFlags: Record<string, boolean>;
    id?: UUID;
  };
  totalPermissions: Permission[];
  userPermissions: Permission[];
};

export type SelectedUserData = {
  companyAccountInfo: UserData['companyAccount'] | Record<string, never>;
  companyInfo: UserData['company'] | Record<string, never>;
  email: string;
  featureFlags: Record<string, boolean>;
  firstName: string;
  isSynced?: boolean;
  isVestigasSupport: boolean;
  lastName: string;
  name: string;
  position: string;
  supportEmail: string | undefined;
  supportPhoneNumber: string | undefined;
  userPermissions: UserData['totalPermissions'];
  userSettings: UserData['settings'] | Record<string, never>;
};

export const selectUserData = (data: UserData): SelectedUserData => ({
  companyAccountInfo: data.companyAccount ?? {},
  companyInfo: data.company ?? {},
  email: data.email ?? '',
  featureFlags: {
    ...data.companyAccount?.data?.featureFlags,
    ...data.settings?.featureFlags,
    // Opt-in only, unlike the spreads above: either level may enable this key,
    // neither may disable what the other enabled.
    [FEATURE_FLAG.MANUAL_INVOICE_CHECKING_PIPELINE]:
      data.companyAccount?.data?.featureFlags?.[
        FEATURE_FLAG.MANUAL_INVOICE_CHECKING_PIPELINE
      ] === true ||
      data.settings?.featureFlags?.[
        FEATURE_FLAG.MANUAL_INVOICE_CHECKING_PIPELINE
      ] === true,
  },
  firstName: data.firstName ?? '',
  isSynced: data.isSynced,
  isVestigasSupport: Boolean(data.isVestigasSupport),
  lastName: data.lastName ?? '',
  name: [data.firstName, data.lastName].filter(Boolean).join(' '),
  position: data.position ?? '',
  supportEmail: data.companyAccount?.data?.supportEmail,
  supportPhoneNumber: data.companyAccount?.data?.supportPhoneNumber,
  userPermissions: data.totalPermissions ?? [],
  userSettings: data.settings ?? {},
});

/**
 * Fetches user data from the API.
 * @param includeCompanyInfo - Whether to include company info in the response.
 * @returns The user data if successful, undefined otherwise.
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/get_all_user_data_user_data_get
 */
export const fetchUserData = async (includeCompanyInfo = true) => {
  const response = await vestigasApi
    .get(ENDPOINT.USER.GET_DATA(includeCompanyInfo))
    .json<UserData>();

  return response;
};

/**
 * Generates the query options for the user data query.
 *
 * @param params - Parameters for the query
 * @param params.includeCompanyInfo - Whether to include company info
 * @param params.options - Additional options for the query
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const userDataQueryOptions = <TData = SelectedUserData>({
  includeCompanyInfo = true,
  options,
}: {
  includeCompanyInfo?: boolean;
  options?: Omit<
    UseQueryOptions<UserData, Error, TData>,
    'queryKey' | 'queryFn'
  >;
} = {}) =>
  queryOptions({
    queryFn: async () => fetchUserData(includeCompanyInfo),
    queryKey: queryKeysUser.getData(includeCompanyInfo),
    select: selectUserData as (data: UserData) => TData,
    staleTime: ms('10m'),
    ...options,
  });

/**
 * Generates the query options for the user data query with authentication guard.
 *
 * **Authentication Guard**: This function includes an `enabled` check based on authentication status
 * to prevent API requests when the user is not authenticated. Uses `skipToken` when not authenticated.
 *
 * @param params - Parameters for the query
 * @param params.isAuthenticated - Whether the user is authenticated (required)
 * @param params.includeCompanyInfo - Whether to include company info
 * @param params.options - Additional options for the query
 * @returns The query options including queryKey, queryFn, enabled, and other settings.
 */
export const getUserDataQueryOptions = ({
  includeCompanyInfo = true,
  isAuthenticated,
  options,
}: {
  includeCompanyInfo?: boolean;
  isAuthenticated: boolean;
  options?: Omit<
    UseQueryOptions<UserData, Error, SelectedUserData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const baseOptions = userDataQueryOptions({ includeCompanyInfo, options });

  return queryOptions({
    ...baseOptions,
    enabled: isAuthenticated && (options?.enabled ?? true),
  });
};

/**
 * React Query based custom hook for getting the data for the current user.
 *
 * **Authentication Guard**: This hook automatically checks if the user is authenticated
 * before making API requests. If no valid access token is found, the query is disabled
 * and no request is made.
 *
 * @param includeCompanyInfo - Whether to include company info in the response
 * @param options - Additional options for the useQuery hook
 * @returns The result of the useQuery hook
 */
export const useQueryUserData = (
  includeCompanyInfo = true,
  options?: Omit<
    UseQueryOptions<UserData, Error, SelectedUserData>,
    'queryKey' | 'queryFn'
  >,
) => {
  const userId = AuthService.getUserIdFromAccessToken();
  const isAuthenticated = Boolean(userId);

  return useQuery(
    getUserDataQueryOptions({
      includeCompanyInfo,
      isAuthenticated,
      options,
    }),
  );
};

/**
 * Suspense-enabled React Query hook for getting the data for the current user.
 *
 * @param includeCompanyInfo - Whether to include company info in the response
 * @returns The result of the useSuspenseQuery hook with guaranteed data
 */
export const useSuspenseQueryUserData = (includeCompanyInfo = true) =>
  useSuspenseQuery(userDataQueryOptions({ includeCompanyInfo }));
